import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { Toast } from '@capacitor/toast';
import { Capacitor } from '@capacitor/core';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { useUser } from '../context/UserContext';
import { getOrCreateDirectConversation } from "../firebase/messagingUtils";
import { setPendingNotificationRedirect } from '../utils/authVerification';

const AUTH_PATHS = new Set([
    '/',
    '/login',
    '/register',
    '/verify-email',
    '/verify-phone',
]);

function isConversationPath(path) {
    return path.startsWith('/direct/') || path.startsWith('/group/');
}

function isStableLoggedInPath(path) {
    if (!path || AUTH_PATHS.has(path)) return false;
    return !isConversationPath(path);
}

function parsePayloadIsGroup(value) {
    return value === true || value === 'true';
}

function buildConversationRoute(conversationId, isGroup) {
    if (!conversationId) return '/messages';
    return isGroup ? `/group/${conversationId}` : `/direct/${conversationId}`;
}

function resolveMessageTargetRoute(conversationId, payloadIsGroup, timeoutMs = 2500) {
    return new Promise((resolve) => {
        let resolved = false;
        let timeoutId = null;

        const finish = (path) => {
            if (resolved) return;
            resolved = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            resolve(path || '/messages');
        };

        if (!conversationId) {
            finish('/messages');
            return;
        }

        timeoutId = setTimeout(() => {
            finish(buildConversationRoute(conversationId, payloadIsGroup));
        }, timeoutMs);

        getDoc(doc(db, 'conversations', conversationId))
            .then((snap) => {
                if (!snap.exists()) {
                    finish('/messages');
                    return;
                }

                const isGroup = snap.data()?.isGroup === true;
                finish(buildConversationRoute(conversationId, isGroup));
            })
            .catch((err) => {
                console.error('Push action conversation lookup failed:', err);
                finish(buildConversationRoute(conversationId, payloadIsGroup));
            });
    });
}

export function BackButtonHandler() {
    const { currentUser } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const currentPathRef = useRef(location.pathname);
    const currentStablePathRef = useRef(null);
    const previousStablePathRef = useRef(null);
    const skipStableRotationRef = useRef(false);
    const lastBackPressRef = useRef(0);

    useEffect(() => {
        currentPathRef.current = location.pathname;

        if (!currentUser || !isStableLoggedInPath(location.pathname)) {
            return;
        }

        if (skipStableRotationRef.current) {
            currentStablePathRef.current = location.pathname;
            previousStablePathRef.current = null;
            skipStableRotationRef.current = false;
            return;
        }

        if (currentStablePathRef.current !== location.pathname) {
            previousStablePathRef.current = currentStablePathRef.current;
            currentStablePathRef.current = location.pathname;
        }
    }, [currentUser, location.pathname]);

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        const handleBackButton = async () => {
            const path = currentPathRef.current;
            const now = Date.now();
            const timeSinceLast = now - lastBackPressRef.current;
            const loggedIn = !!currentUser;

            if (loggedIn) {
                if (isConversationPath(path)) {
                    navigate('/messages', { replace: true, state: null });
                    return;
                }

                if (isStableLoggedInPath(path)) {
                    const previousStablePath = previousStablePathRef.current;
                    if (previousStablePath && previousStablePath !== path) {
                        skipStableRotationRef.current = true;
                        previousStablePathRef.current = null;
                        currentStablePathRef.current = previousStablePath;
                        navigate(previousStablePath, { replace: true, state: null });
                        return;
                    }
                }

                if (timeSinceLast < 2000) {
                    CapacitorApp.exitApp();
                } else {
                    lastBackPressRef.current = now;
                    await Toast.show({
                        text: 'Press back again to exit BlueWire',
                        duration: 'short',
                        position: 'bottom'
                    });
                }
                return;
            }

            if (AUTH_PATHS.has(path)) {
                if (timeSinceLast < 2000) {
                    CapacitorApp.exitApp();
                } else {
                    lastBackPressRef.current = now;
                    await Toast.show({
                        text: 'Press back again to exit BlueWire',
                        duration: 'short',
                        position: 'bottom'
                    });
                }
                return;
            }

            navigate('/login');
        };

        let backListener;
        const setupBackListener = async () => {
            backListener = await CapacitorApp.addListener('backButton', handleBackButton);
        };
        setupBackListener();

        return () => backListener?.remove();
    }, [currentUser, navigate]);

    return null;
}

export function PushActionHandler() {
    const navigate = useNavigate();
    const { currentUser, loading } = useUser();
    const replaceCallNavigation =
        Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    const isActiveRef = useRef(true);

    useEffect(() => {
        isActiveRef.current = true;
        return () => {
            isActiveRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        let actionListener;
        const setupActionListener = async () => {
            actionListener = await PushNotifications.addListener('pushNotificationActionPerformed', async (notification) => {
                const data = notification.notification?.data;
                if (data?.type === 'message') {
                    const conversationId = data?.conversationId || null;
                    const payloadIsGroup = parsePayloadIsGroup(data?.isGroup);
                    const activeUid = currentUser?.uid || auth.currentUser?.uid || null;
                    const fallbackRoute = buildConversationRoute(
                        conversationId,
                        payloadIsGroup
                    );

                    if (loading) {
                        setPendingNotificationRedirect(fallbackRoute, activeUid);
                        if (!activeUid) {
                            navigate('/login', {
                                replace: true,
                                state: { redirectTo: fallbackRoute },
                            });
                        }
                        return;
                    }

                    if (!activeUid) {
                        setPendingNotificationRedirect(fallbackRoute);
                        navigate('/login', {
                            replace: true,
                            state: { redirectTo: fallbackRoute },
                        });
                        return;
                    }

                    const targetRoute = await resolveMessageTargetRoute(
                        conversationId,
                        payloadIsGroup
                    );

                    if (!isActiveRef.current) {
                        return;
                    }

                    if (targetRoute !== '/messages') {
                        setPendingNotificationRedirect(targetRoute, activeUid);
                    }
                    navigate(targetRoute, { replace: true });
                    return;
                }

                if (data?.type === 'intel') {
                    const targetRoute = '/intel';
                    const activeUid = currentUser?.uid || auth.currentUser?.uid || null;

                    if (loading) {
                        setPendingNotificationRedirect(targetRoute, activeUid);
                        if (!activeUid) {
                            navigate('/login', {
                                replace: true,
                                state: { redirectTo: targetRoute },
                            });
                        }
                        return;
                    }

                    if (!activeUid) {
                        setPendingNotificationRedirect(targetRoute);
                        navigate('/login', {
                            replace: true,
                            state: { redirectTo: targetRoute },
                        });
                        return;
                    }

                    setPendingNotificationRedirect(targetRoute, activeUid);
                    navigate(targetRoute, { replace: true });
                    return;
                }

                if (data?.type === 'incoming_call' && data?.callId && data?.from && currentUser?.uid) {
                    try {
                        const conversationId = await getOrCreateDirectConversation(currentUser.uid, data.from);
                        let incomingCallPrefetch = null;
                        try {
                            const snap = await getDoc(doc(db, "calls", data.callId));
                            if (snap.exists()) {
                                incomingCallPrefetch = { id: snap.id, ...snap.data() };
                            }
                        } catch (err) {
                            console.warn("Push action prefetch failed:", err);
                        }
                        navigate(`/direct/${conversationId}`, {
                            state: { autoAcceptCallId: data.callId, incomingCallPrefetch },
                            replace: replaceCallNavigation,
                        });
                    } catch (err) {
                        console.error("Push action navigate failed:", err);
                        navigate('/messages');
                    }
                }
            });
        };
        setupActionListener();

        return () => actionListener?.remove?.();
    }, [navigate, currentUser?.uid, loading, replaceCallNavigation]);

    return null;
}

export function NativeCallHandler({ onConnectingChange }) {
    const navigate = useNavigate();
    const { currentUser } = useUser();
    const queueRef = useRef([]);
    const drainingRef = useRef(false);
    const nativeQueueRef = useRef([]);
    const nativeDrainingRef = useRef(false);
    const replaceCallNavigation =
        Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

    const processAction = useCallback(
        async ({ action, callId, callerId, conversationId }) => {
            if (!currentUser?.uid || !callId) return;

            if (action === "accept") {
                if (!callerId) return;
                try {
                    onConnectingChange?.({ callId });
                    let convoId = conversationId;
                    if (!convoId) {
                        convoId = await getOrCreateDirectConversation(
                            currentUser.uid,
                            callerId
                        );
                    }
                    let incomingCallPrefetch = null;
                    try {
                        const snap = await getDoc(doc(db, "calls", callId));
                        if (snap.exists()) {
                            incomingCallPrefetch = { id: snap.id, ...snap.data() };
                        }
                    } catch (err) {
                        console.warn("Failed to prefetch call doc:", err);
                    }

                    navigate(`/direct/${convoId}`, {
                        state: { autoAcceptCallId: callId, incomingCallPrefetch },
                        replace: replaceCallNavigation,
                    });
                    onConnectingChange?.(null);
                } catch (err) {
                    console.error("Failed to handle native accept action:", err);
                    onConnectingChange?.(null);
                }
            }

            if (action === "decline") {
                try {
                    await updateDoc(doc(db, "calls", callId), {
                        status: "ended",
                        reason: "declined",
                        endedAt: serverTimestamp()
                    });
                } catch (err) {
                    console.warn("Decline update failed, trying merge set:", err);
                    try {
                        await setDoc(
                            doc(db, "calls", callId),
                            {
                                status: "ended",
                                reason: "declined",
                                endedAt: serverTimestamp()
                            },
                            { merge: true }
                        );
                    } catch (err2) {
                        console.error("Failed to mark call declined:", err2);
                    }
                }
            }

            if (action === "end") {
                try {
                    await updateDoc(doc(db, "calls", callId), { status: "ended" });
                } catch (err) {
                    console.warn("End update failed, trying merge set:", err);
                    try {
                        await setDoc(
                            doc(db, "calls", callId),
                            { status: "ended" },
                            { merge: true }
                        );
                    } catch (err2) {
                        console.error("Failed to mark call ended:", err2);
                    }
                }
            }
        },
        [navigate, currentUser?.uid, onConnectingChange, replaceCallNavigation]
    );

    const drainQueue = useCallback(async () => {
        if (!currentUser?.uid) return;
        if (drainingRef.current) return;
        drainingRef.current = true;
        try {
            while (queueRef.current.length > 0) {
                const item = queueRef.current.shift();
                // eslint-disable-next-line no-await-in-loop
                await processAction(item);
            }
        } finally {
            drainingRef.current = false;
        }
    }, [currentUser?.uid, processAction]);

    useEffect(() => {
        const handler = (e) => {
            const detail = e.detail || {};
            if (detail.action !== "accept" && detail.action !== "decline" && detail.action !== "end") return;
            if (detail.action === "accept" || detail.action === "decline" || detail.action === "end") {
                queueRef.current.push(detail);
                drainQueue();
            }
        };

        window.addEventListener("performNavigation", handler);
        if (Array.isArray(window.__pendingPerformNavigation)) {
            queueRef.current.push(...window.__pendingPerformNavigation);
            window.__pendingPerformNavigation = [];
            drainQueue();
        }
        drainQueue();
        return () => window.removeEventListener("performNavigation", handler);
    }, [drainQueue]);

    useEffect(() => {
        if (currentUser?.uid) {
            drainQueue();
        }
    }, [currentUser?.uid, drainQueue]);

    const drainNativeQueue = useCallback(async () => {
        if (!currentUser?.uid) return;
        if (nativeDrainingRef.current) return;
        nativeDrainingRef.current = true;
        try {
            while (nativeQueueRef.current.length > 0) {
                const item = nativeQueueRef.current.shift();
                // eslint-disable-next-line no-await-in-loop
                await processAction(item);
            }
        } finally {
            nativeDrainingRef.current = false;
        }
    }, [currentUser?.uid, processAction]);

    useEffect(() => {
        const handler = (e) => {
            const detail = e.detail || {};
            if (detail.action !== "accept" && detail.action !== "decline" && detail.action !== "end") return;
            nativeQueueRef.current.push(detail);
            drainNativeQueue();
        };

        window.addEventListener("nativeCallAction", handler);

        if (Array.isArray(window.__pendingNativeCallActions)) {
            nativeQueueRef.current.push(...window.__pendingNativeCallActions);
            window.__pendingNativeCallActions = [];
            drainNativeQueue();
        }
        drainNativeQueue();

        return () => window.removeEventListener("nativeCallAction", handler);
    }, [drainNativeQueue]);

    useEffect(() => {
        if (currentUser?.uid) {
            drainNativeQueue();
        }
    }, [currentUser?.uid, drainNativeQueue]);

    return null;
}
